/**
 * Keeping score for a game of klaverjassen played with real cards.
 *
 * A separate shape from `ScoreGame`, for the same reason `HeartsGame` is a
 * separate class from `LiveGame`: what you write down is different. There is no
 * bid and no tricks-per-player here — there are two teams, a trump, and what
 * each side took.
 *
 * The input surface is deliberately small. Per deal: which team played, the
 * trump, and one number. Roem only when there was any, and pit is not asked at
 * all — 162 can only be reached by taking every card, so the app already knows.
 */

import type { Seat, Suit } from './game';
import type { KlaverjasOptions } from '../game-logic/klaverjas';

export interface KlaverjasDealResult {
  points: [number, number];
  nat: boolean;
  pitTeam: 0 | 1 | null;
}

export interface KlaverjasDealEntry {
  /** 1-based. */
  dealNo: number;
  /** Rotates one seat per deal, like everywhere else. */
  dealerSeat: number;
  /** The team that chose trump. Null until entered. */
  playingTeam: 0 | 1 | null;
  trump: Suit | null;
  /**
   * Card points team 0 took, *including* the ten for the last trick. Team 1
   * gets 162 minus this, so only one number has to be typed.
   */
  takenTeam0: number | null;
  /** Usually [0, 0]; only touched when there was roem. */
  roem: [number, number];
  status: 'entering' | 'done';
  /**
   * Stored rather than recomputed on read, so a later change to the rules or
   * the options never rewrites the score of a game that is already finished.
   * The same reason `ScoringConfig` lives on the game.
   */
  result: KlaverjasDealResult | null;
}

export interface KlaverjasScoreGame {
  id: string;
  mode: 'scorekeeper';
  /** The discriminant. Boerenbridge games carry `game: 'boerenbridge'`. */
  game: 'klaverjas';
  createdAt: string;
  updatedAt: string;
  finishedAt: string | null;
  status: 'in-progress' | 'finished';
  options: KlaverjasOptions;
  /** Exactly four, seats 0..3. Team is `seat % 2`, so partners sit across. */
  seats: Seat[];
  deals: KlaverjasDealEntry[];
}

export interface KlaverjasTeamStanding {
  team: 0 | 1;
  names: [string, string];
  total: number;
  /** How many deals this team chose trump in, and how often it went wet. */
  played: number;
  nat: number;
}

/** Running totals after every completed deal. Highest wins. */
export function klaverjasTotals(game: KlaverjasScoreGame): [number, number] {
  return game.deals.reduce<[number, number]>(
    (totals, deal) => {
      if (!deal.result) return totals;
      return [totals[0] + deal.result.points[0], totals[1] + deal.result.points[1]];
    },
    [0, 0]
  );
}

export function klaverjasStandings(game: KlaverjasScoreGame): KlaverjasTeamStanding[] {
  const totals = klaverjasTotals(game);

  return ([0, 1] as const).map((team) => ({
    team,
    names: [game.seats[team]?.displayName ?? '?', game.seats[team + 2]?.displayName ?? '?'] as [
      string,
      string,
    ],
    total: totals[team],
    played: game.deals.filter((deal) => deal.result && deal.playingTeam === team).length,
    nat: game.deals.filter((deal) => deal.result?.nat && deal.playingTeam === team).length,
  }));
}
